import React, { useCallback, useEffect, useMemo, useState } from 'react'
import { useSelector } from 'react-redux'
import B2BLayout from '../../../layouts/b2b'
import { Form, message, Skeleton, Result } from 'antd'
import fetchAPI, { IHttpError } from '../../../../lib/utils/fetch-api'
import { useParams } from 'react-router-dom'
import { MapContent, MapTitle, TaskWrapper } from '../../../pages/card-employee/styles'
import { useForm } from 'antd/es/form/Form'
import './style.css'
import {
  Permission,
  PermissionValue,
  isPermissionEnabledForTenant,
  isPermissionVisible,
} from './utils'
import { RoleForm } from './components/RoleForm'
import { PermissionsEditor } from './components/PermissionsEditor'
import { ResultStatusType } from 'antd/es/result'

interface IError {
  statusCode: ResultStatusType
  message: string
}

/**
 * Компонент создания/редактирования роли
 * Отображает форму с базовыми настройками роли и редактором прав доступа
 */
export const AdminEditRole = () => {
  const { id } = useParams() as any
  const [loading, setLoading] = useState<boolean>(true)
  const [accessError, setAccessError] = useState<IError | null>(null)
  const [form] = useForm()
  const [permissions, setPermissions] = useState<Permission[]>([])
  const [currentPermissions, setCurrentPermissions] = useState<PermissionValue[]>([])
  const sessionData = useSelector((state: any) => state?.session?.sessionData)
  const activeModules: string[] = sessionData?.activeModules || []
  const allowedMenuKeys: string[] | null = sessionData?.allowedMenuKeys ?? null
  const visiblePermissions = permissions.filter(permission =>
    isPermissionEnabledForTenant(permission.name, { activeModules, allowedMenuKeys }),
  )

  useEffect(() => {
    const formPermissions = form.getFieldValue('permissions') || []
    setCurrentPermissions(formPermissions)
  }, [form])

  /**
   * Хлебные крошки
   */
  const breadcrumbs = useMemo(
    () => [
      {
        href: '/admin',
        title: 'Админ панель',
      },
      {
        href: '/admin/roles',
        title: 'Список ролей',
      },
    ],
    [location.pathname],
  )

  useEffect(() => {
    getData()
    getPermissions()
  }, [])

  const getPermissions = useCallback(async () => {
    try {
      const data = await fetchAPI(`/api/roles/permissions-list`, { method: 'GET' })
      if (Array.isArray(data)) {
        setPermissions(data)
      }
    } catch (err) {
      const statusCode = ((err as IHttpError)?.statusCode || 'error') as ResultStatusType
      const message = (err as IHttpError)?.message || 'Не удалось получить список прав доступа'
      setAccessError({ message, statusCode })
      console.error('Не удалось получить список уровней доступа', { err })
    }
  }, [])

  const getData = useCallback(async () => {
    try {
      if (!id || id === 'new') {
        form.setFieldsValue({
          is_active: true,
          permissions: [],
        })
        setAccessError(null)
        setLoading(false)
        return
      }

      const data = await fetchAPI(`/api/roles/${id}`, { method: 'GET' })
      const formData = { ...data?.role, ...{ permissions: data?.role_permissions } }
      setCurrentPermissions(formData.permissions || [])
      form.setFieldsValue(formData)
      setAccessError(null)
    } catch (err) {
      const statusCode = ((err as IHttpError)?.statusCode || 'error') as ResultStatusType
      const message = (err as IHttpError)?.message || 'Не удалось загрузить данные роли'
      setAccessError({ message, statusCode })
      console.error('Не удалось получить данные', { err })
    } finally {
      setLoading(false)
    }
  }, [id, form])

  const onSubmit = async () => {
    try {
      const values = await form.validateFields()

      const catalogIds = new Set(visiblePermissions.map(permission => permission.id))

      const getPermissionValue = (permissionId: number) => {
        const permission = currentPermissions.find(item => item.id === permissionId)
        return permission?.value !== undefined ? permission.value : null
      }

      const isPermissionVisibleInForm = (permissionId: number) => {
        const permissionData = visiblePermissions.find(p => p.id === permissionId)
        return permissionData
          ? isPermissionVisible(permissionData, visiblePermissions, getPermissionValue)
          : false
      }

      const fromVisible = currentPermissions.filter(permission => {
        if (!catalogIds.has(permission.id)) return false
        if (!isPermissionVisibleInForm(permission.id)) return false
        return permission.value !== null && permission.value !== false
      })
      const preservedHidden = currentPermissions.filter(permission => {
        if (catalogIds.has(permission.id)) return false
        return permission.value !== null && permission.value !== false
      })

      const data = { ...values, permissions: [...fromVisible, ...preservedHidden] }
      const route = id === 'new' ? 'new' : `update/${id}`
      await fetchAPI(`/api/roles/${route}`, { method: 'POST', body: JSON.stringify(data) })
        .then(() => {
          message.success('Настройки успешно сохранены')
          // history.push('/admin/roles')
        })
        .catch(err => {
          const errorMessage = (err as IHttpError)?.message || 'Не удалось сохранить настройки'
          console.error('Не удалось сохранить данные', { err })
          message.error(errorMessage)
        })
    } catch (errorInfo) {
      const err = form.getFieldsError()
      if (err) {
        const fieldName = err?.[0]?.['name']?.[0]
        if (fieldName) {
          form.scrollToField(fieldName, {
            behavior: 'smooth',
            block: 'center',
          })
        }
      }
      console.error('Failed:', { errorInfo })
    }
  }

  const handlePermissionsChange = (updatedPermissions: PermissionValue[]) => {
    setCurrentPermissions(updatedPermissions)
    form.setFieldsValue({ permissions: updatedPermissions })
  }

  return (
    <B2BLayout breadcrumbs={breadcrumbs}>
      {loading && <Skeleton active />}
      {!loading && accessError && (
        <Result status={accessError.statusCode} title="Ошибка" subTitle={accessError.message} />
      )}
      {!loading && !accessError && (
        <>
          <TaskWrapper>
            <MapTitle>
              {form.getFieldValue('name') ? (
                <>
                  Редактирование роли <span>{`${form.getFieldValue('name')}`}</span>
                </>
              ) : (
                <>Создание новой роли</>
              )}
            </MapTitle>
          </TaskWrapper>
          <MapContent>
            <RoleForm form={form} loading={loading} onSubmit={onSubmit}>
              <Form.Item
                label="Права доступа"
                wrapperCol={{ span: 18 }}
                style={{ marginBottom: 0 }}
              >
                <PermissionsEditor
                  permissions={visiblePermissions}
                  currentPermissions={currentPermissions}
                  onPermissionChange={handlePermissionsChange}
                />
              </Form.Item>
            </RoleForm>
          </MapContent>
        </>
      )}
    </B2BLayout>
  )
}
